<template>
  <article class="card cl-files-card">
    <header class="cl-card-head">
      <div>
        <p class="eyebrow">Detected logs</p>
        <h3>Log files <span class="cl-count">{{ files.length }}</span></h3>
      </div>
      <div class="page-actions">
        <AppDropdown
          size="sm"
          label="Sort"
          :model-value="sortKey"
          :options="sortOptions"
          @update:model-value="changeSort"
        />
        <button
          class="btn-icon-outline"
          type="button"
          :aria-label="sortDirection === 'desc' ? 'Newest first' : 'Oldest first'"
          :title="sortDirection === 'desc' ? 'Newest first' : 'Oldest first'"
          @click="flipDirection"
        >
          <i class="fa-solid" :class="sortDirection === 'desc' ? 'fa-arrow-down-long' : 'fa-arrow-up-long'"></i>
        </button>
      </div>
    </header>

    <div class="cl-files-toolbar">
      <label class="cl-switch">
        <input
          type="checkbox"
          :checked="onlyWowLogs"
          @change="$emit('toggleOnlyWowLogs', $event.target.checked)"
        >
        <span>Only WoWCombatLog files</span>
      </label>
      <label class="cl-switch">
        <input
          type="checkbox"
          :checked="autoAnalyzeNewest"
          @change="$emit('toggleAutoAnalyze', $event.target.checked)"
        >
        <span>Follow the newest log</span>
      </label>
    </div>

    <p v-if="!files.length" class="cl-empty-line">
      No log files yet. Connect a folder or drop a log file to get started.
    </p>

    <ul v-else class="cl-file-list">
      <li
        v-for="file in files"
        :key="file.id"
        class="cl-file-row"
        :class="{ 'is-selected': file.id === selectedFileId, 'is-missing': !file.available }"
      >
        <button class="cl-file-main" type="button" @click="$emit('select', file.id)">
          <span class="cl-file-title">
            <strong>{{ shortName(file.name) }}</strong>
            <span class="cl-status" :class="`is-${file.status}`">{{ statusLabel(file) }}</span>
          </span>
          <span class="cl-file-meta">
            <span><i class="fa-regular fa-calendar"></i> {{ formatDate(file.lastModified) }}</span>
            <span><i class="fa-solid fa-database"></i> {{ formatSize(file.size) }}</span>
            <span v-if="dungeonOf(file)"><i class="fa-solid fa-dungeon"></i> {{ dungeonOf(file) }}</span>
            <span v-if="file.summary"><i class="fa-solid fa-khanda"></i> {{ fightCount(file) }} fights</span>
            <span v-if="file.summary"><i class="fa-solid fa-skull"></i> {{ plural(file.summary.deathCount, 'death') }}</span>
            <span v-if="!file.available" class="cl-warn-text">File is no longer in the folder</span>
            <span v-if="file.error" class="cl-warn-text">{{ file.error }}</span>
          </span>
          <span v-if="progressOf(file)" class="cl-progress">
            <span class="cl-progress-bar">
              <span class="cl-progress-fill" :style="{ width: progressPercent(file) + '%' }"></span>
            </span>
            <small>{{ progressLabel(file) }}</small>
          </span>
        </button>

        <span class="cl-file-actions">
          <button
            v-if="file.available && file.status !== 'processing'"
            class="btn-icon-outline"
            type="button"
            aria-label="Analyze"
            title="Analyze new events"
            @click="$emit('analyze', file.id)"
          >
            <i class="fa-solid fa-play"></i>
          </button>
          <button
            v-if="file.status === 'processing'"
            class="btn-icon-outline"
            type="button"
            aria-label="Cancel analysis"
            title="Cancel analysis"
            @click="$emit('cancel', file.id)"
          >
            <i class="fa-solid fa-stop"></i>
          </button>
          <button
            v-if="file.available && file.offset > 0"
            class="btn-icon-outline"
            type="button"
            aria-label="Re-analyze from scratch"
            title="Re-analyze from scratch"
            @click="$emit('reanalyze', file.id)"
          >
            <i class="fa-solid fa-rotate-right"></i>
          </button>
          <button
            class="btn-icon-outline"
            type="button"
            aria-label="Forget stored analysis"
            title="Forget stored analysis"
            @click="$emit('remove', file.id)"
          >
            <i class="fa-solid fa-trash-can"></i>
          </button>
        </span>
      </li>
    </ul>

    <footer v-if="files.length" class="cl-files-foot">
      <span class="muted-line">
        Analyses are stored locally{{ storageLabel }}. Logs themselves are never copied or uploaded.
        {{ cacheLabel }}
      </span>
      <div class="cl-files-foot-actions">
        <AppDropdown
          size="sm"
          label="Report cache"
          align="right"
          :model-value="reportCacheMb"
          :options="cacheOptions"
          @update:model-value="$emit('setCacheBudget', $event)"
        />
        <button class="btn-sm-outline" type="button" @click="$emit('clearCache')">Free cached reports</button>
        <button class="btn-sm-outline" type="button" @click="$emit('clearStored')">Clear stored analyses</button>
      </div>
    </footer>
  </article>
</template>

<script>
import AppDropdown from '../AppDropdown.vue'
import { formatNumber, formatSize, plural } from '../../services/combatLog/format'

const STATUS_LABELS = {
  new: 'New',
  processing: 'Processing',
  ready: 'Ready',
  updated: 'Updated',
  error: 'Error',
}

export default {
  name: 'CombatLogFileList',
  components: { AppDropdown },
  props: {
    files: { type: Array, default: () => [] },
    selectedFileId: { type: String, default: '' },
    sortKey: { type: String, default: 'lastModified' },
    sortDirection: { type: String, default: 'desc' },
    progress: { type: Object, default: () => ({}) },
    onlyWowLogs: { type: Boolean, default: true },
    autoAnalyzeNewest: { type: Boolean, default: true },
    storage: { type: Object, default: () => ({}) },
    reportCache: { type: Object, default: () => ({}) },
    reportCacheMb: { type: Number, default: 64 },
  },
  emits: [
    'select', 'analyze', 'reanalyze', 'remove', 'cancel', 'sort',
    'toggleOnlyWowLogs', 'toggleAutoAnalyze', 'clearStored',
    'setCacheBudget', 'clearCache',
  ],
  computed: {
    sortOptions() {
      return [
        { value: 'lastModified', label: 'Date' },
        { value: 'name', label: 'Name' },
        { value: 'size', label: 'Size' },
        { value: 'status', label: 'Status' },
      ]
    },
    storageLabel() {
      if (!this.storage || !this.storage.usage) return ''
      return ` (${formatSize(this.storage.usage)} used)`
    },
    cacheOptions() {
      return [16, 32, 64, 128, 256].map((mb) => ({ value: mb, label: `${mb} MB` }))
    },
    cacheLabel() {
      const cache = this.reportCache
      if (!cache || !cache.reports) return ''
      return `Cached reports: ${formatSize(cache.bytes)} across ${plural(cache.files, 'log')}; the oldest are dropped past the limit.`
    },
  },
  methods: {
    changeSort(key) {
      this.$emit('sort', { key, direction: this.sortDirection })
    },
    flipDirection() {
      this.$emit('sort', { key: this.sortKey, direction: this.sortDirection === 'desc' ? 'asc' : 'desc' })
    },
    shortName(name) {
      const trimmed = name.replace(/^WoWCombatLog-?/, '').replace(/\.txt$/i, '')
      return trimmed || name
    },
    statusLabel(file) {
      if (!file.available) return 'Missing'
      return STATUS_LABELS[file.status] || file.status
    },
    dungeonOf(file) {
      const summary = file.summary
      if (!summary) return ''
      if (summary.challengeRuns && summary.challengeRuns.length) {
        const run = summary.challengeRuns[summary.challengeRuns.length - 1]
        const level = run.meta && run.meta.keystoneLevel ? ` +${run.meta.keystoneLevel}` : ''
        return `${run.name}${level}`
      }
      if (summary.primaryZone) return summary.primaryZone.name
      return summary.zone ? summary.zone.name : ''
    },
    fightCount(file) {
      const summary = file.summary
      if (!summary) return 0
      return (summary.challengeRuns ? summary.challengeRuns.length : 0)
        + (summary.encounters ? summary.encounters.length : 0)
    },
    progressOf(file) {
      return this.progress[file.id] || null
    },
    progressPercent(file) {
      const entry = this.progressOf(file)
      if (!entry || !entry.size) return 0
      return Math.min(100, Math.round((entry.offset / entry.size) * 100))
    },
    progressLabel(file) {
      const entry = this.progressOf(file)
      if (!entry) return ''
      if (!entry.size) return 'Reading...'
      return `${formatSize(entry.offset)} of ${formatSize(entry.size)} - ${formatNumber(entry.events)} events`
    },
    formatNumber,
    formatSize,
    plural,
    formatDate(ms) {
      if (!ms) return '-'
      return new Date(ms).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>
